import { Chip, Stack, Tooltip, Box } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { useMemo, useState } from "react";
import useOpen from "utils/useOpen";
import EntitiesDialog from "./EntitiesDialog";
import entityService from "services/entities-service";
import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";

export default function AllEntities({ permissions }) {
  const { label } = useLanguage();
  const open = useOpen({ manageDialog: false, alert: false });
  const can = usePermissionMonitor();

  const [entities, setEntities] = useState(null);
  const [lastFilter, setLastFilter] = useState(null);

  async function handleAddEntity(newElement) {
    console.log("NEW ELEMENT: ", newElement);
    // return;
    const [response, data] = await entityService.createEntity(newElement);
    if (response.ok) {
      const [response, data] = await entityService.getEntities(lastFilter);
      if (response.ok) {
        setEntities(data);
      }
      open.change("create", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateEntity(newElement) {
    console.log("NEW ELEMENT: ", newElement);
    // return;
    const [response, data] = await entityService.updateEntity(
      newElement.id,
      newElement,
    );
    if (response.ok) {
      const [response, data] = await entityService.getEntities(lastFilter);
      if (response.ok) {
        setEntities(data);
      }
      open.change("update", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteEntity(id) {
    const [response, data] = await entityService.removeEntity(id);
    if (response.ok) {
      const [response, data] = await entityService.getEntities(lastFilter);
      if (response.ok) {
        setEntities(data);
      }
      open.change("alert", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  const rows = useMemo(() => {
    if (!entities?.length)
      return entities?.length === 0
        ? [
            {
              name: label("no-entities-were-found"),
            },
          ]
        : [
            {
              name: "Loading...",
            },
          ];
    return entities.map((entity) => {
      return {
        name: <MDTypography variant="inherit">{entity.name}</MDTypography>,

        account: (
          <MDTypography variant="inherit">
            {entity.account?.name || label("all")}
          </MDTypography>
        ),

        actions:
          (!entity.accountId && can("isAdmin")) || entity.accountId ? (
            <MDBox
              display="flex"
              flexDirection="row"
              justifyContent="end"
              alignItems="center"
            >
              {permissions.update && (
                <Tooltip title={label("Edit")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("update", {
                        entity,
                      });
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {permissions.delete && (
                <Tooltip title={label("Delete")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("alert", {
                        entity,
                      });
                    }}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          ) : null,
      };
    });
  }, [entities, permissions]);

  const columns = useMemo(() => {
    const columns = [
      {
        Header: label("name-singular"),
        accessor: "name",
      },
      {
        Header: label("account-singular"),
        accessor: "account",
        width: "100%",
      },
    ];

    if (permissions.update || permissions.delete) {
      columns.push({
        Header: "",
        accessor: "actions",
        align: "right",
      });
    }

    return columns;
  }, [permissions]);

  return (
    <>
      {open.get("alert") ? (
        <AlertDialog
          open={Math.random()}
          setOpen={(v) => {
            open.change("alert", v);
          }}
          title={label("delete-alert-title")}
          content={label("delete-alert-message")}
          onOk={async () => {
            await handleDeleteEntity(open.get("alert").entity.id);
          }}
        />
      ) : null}
      {open.get("create") ? (
        <EntitiesDialog
          mode="create"
          isOpen={open.get("create") ? true : false}
          onSubmit={handleAddEntity}
          onClose={() => open.change("create", false)}
          data={null}
        />
      ) : null}
      {open.get("update") ? (
        <EntitiesDialog
          mode="edit"
          isOpen={open.get("update") ? true : false}
          onSubmit={handleUpdateEntity}
          onClose={() => open.change("update", false)}
          data={open.get("update")}
        />
      ) : null}

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("entity-plural")}{" "}
                  {permissions.get ? `(${entities?.length || 0})` : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      open.change("create", true);
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-entity-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-entities"
                      internalLoading={!Boolean(entities)}
                      fullWidth
                      setData={setEntities}
                      onSearch={async (request, callback) => {
                        setLastFilter(request);
                        callback(await entityService.getEntities(request));
                      }}
                      label={label("search-entities-label")}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(entities)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
